import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { HeaderTitle } from '@mylighthouse/prism-ember/components/prism/card';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import LoadingBar from 'lumen/components/general/loading-bar';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import { timeout } from 'lumen/utils/waiters';

export interface ParityInsightBreakdownBlockTitleParams {
  blockDisplayName: string;
  isLoading?: boolean;
  searchValues?: {
    isSearchActive: boolean;
    searchValue: string;
  };
  segmentationName?: string;
  updateIsSearchActive: (value: boolean) => void;
  updateSearchValue: (value: string) => void;
}

interface ParityInsightBreakdownBlockTitleSignature {
  Args: {
    cardHeader: {
      title?: HeaderTitle;
    };
    disabledTooltipMessage: string;
    isDisabled?: boolean;
    params?: ParityInsightBreakdownBlockTitleParams;
  };
}

export default class ParityInsightBreakdownBlockTitle extends Component<ParityInsightBreakdownBlockTitleSignature> {
  @tracked tempSearchValue = '';

  /**
   * This updates the search value in the controller
   * We keep using the temp search value at all times and assume nothing went wrong updating the value
   * If something did go wrong, we'll send the correct value down to the controller next time
   */
  updateSearchValueTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      const { params } = this.args;

      this.tempSearchValue = (event.target as HTMLInputElement).value;
      await timeout();
      params?.updateSearchValue(this.tempSearchValue);
    },
  );

  @action enableSearch(enable: boolean): void {
    const { params } = this.args;

    params?.updateIsSearchActive(enable);
  }

  <template>
    <div class="title-box flex items-center">
      {{#if @params.isLoading}}
        <LoadingBar />
      {{else if @params.searchValues.isSearchActive}}
        <div class="flex flex-grow">
          <PrismInput
            @autofocus={{true}}
            @iconAfter={{hash name="search"}}
            @onInput={{this.updateSearchValueTask.perform}}
            @placeholder={{t "general.search_placeholder"}}
            @removePadding={{true}}
            @value={{this.tempSearchValue}}
            {{outsideClickHandler (fn this.enableSearch false)}}
          />
        </div>
      {{else}}
        <div class="flex items-center gap-100">
          <@cardHeader.title class="truncate">
            {{@params.blockDisplayName}}
          </@cardHeader.title>

          {{#if @isDisabled}}
            <span>
              <PrismBadge
                @iconName="circle-information-outline"
                @label={{t "general.filtering_unavailable"}}
              />
              <PrismTooltip @text={{@disabledTooltipMessage}} />
            </span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
